import { type TFunction } from 'i18next';
import { SecurityScoreNameEnum } from '../../enums';
import { ISecurityScoreOption, TSecurityScoreName } from './interfaces';

/** Recommended length */
const REC_LENGTH = 20;
const REC_MIN_UNIQ_CHARS = 10;
const UNIQUE_FLAT_RATE_COEF = 0.5;
/** lower, upper, digits, special */
const CHAR_SETS_COUNT = 4;
/** e.g. We need at least 2 lowercase chars to fully satisfy lowercase check */
const CHAR_SET_UNIQUE_RATIO = 0.5;
const LENGTH_OVERFLOW_PER_CHAR = 0.125;

const LENGTH_COEF = 1 / REC_LENGTH;
const UNIQUE_CHAR_COEF = 1 / REC_MIN_UNIQ_CHARS;

export const getSecurityScore = (password: string) => {
  const lengthCoef = Math.min(password.length * LENGTH_COEF, 1);

  /** Length overflow */
  const lengthOverflow = password.length - REC_LENGTH;
  const lengthOverflowFlatPercent = lengthOverflow > 0
    ? (lengthOverflow * LENGTH_OVERFLOW_PER_CHAR)
    : 0;

  /** Uniqueness */
  const uniqueChars = new Set(password.split(''));
  const uniqueCharsScore = Math.min(UNIQUE_CHAR_COEF * uniqueChars.size, 1);
  const uniqFlatRate = (1 - uniqueCharsScore) * UNIQUE_FLAT_RATE_COEF;

  const hasLowerCase = Array.from(password.matchAll(/[a-z]/g)).length;
  const hasUpperCase = Array.from(password.matchAll(/[A-Z]/g)).length;
  const hasDigits = Array.from(password.matchAll(/[0-9]/g)).length;
  const hasSpecChars = Array.from(password.matchAll(/[!@#$%^&*()_+=-?<>'"` ~]/g)).length;
  const diversityCoef = (
    Math.min(hasLowerCase * CHAR_SET_UNIQUE_RATIO, 1)
      + Math.min(hasUpperCase * CHAR_SET_UNIQUE_RATIO, 1)
      + Math.min(hasDigits * CHAR_SET_UNIQUE_RATIO, 1)
      + Math.min(hasSpecChars * CHAR_SET_UNIQUE_RATIO, 1)
  ) / CHAR_SETS_COUNT;

  const totalScore = diversityCoef
    * uniqueCharsScore
    * lengthCoef
    + lengthOverflowFlatPercent
    - uniqFlatRate;
  const normalizedScore = Math.max(Math.min(totalScore, 1), 0) * 100;

  return Math.ceil(normalizedScore);
};

const SECURITY_SCORE_STRENGTH = {
  [SecurityScoreNameEnum.VeryWeak]: { minStrength: 0, maxStrength: 20 },
  [SecurityScoreNameEnum.Weak]: { minStrength: 21, maxStrength: 40 },
  [SecurityScoreNameEnum.Medium]: { minStrength: 41, maxStrength: 60 },
  [SecurityScoreNameEnum.Strong]: { minStrength: 61, maxStrength: 80 },
  [SecurityScoreNameEnum.VeryStrong]: { minStrength: 81, maxStrength: 100 },
};

export const getSecurityScoreOptions = (t: TFunction): ISecurityScoreOption[] => [
  { label: t('securityScore.veryWeak'), value: SecurityScoreNameEnum.VeryWeak },
  { label: t('securityScore.weak'), value: SecurityScoreNameEnum.Weak },
  { label: t('securityScore.medium'), value: SecurityScoreNameEnum.Medium },
  { label: t('securityScore.strong'), value: SecurityScoreNameEnum.Strong },
  { label: t('securityScore.veryStrong'), value: SecurityScoreNameEnum.VeryStrong },
];

export const getSecurityScoreStrength = (
  securityScoreName: TSecurityScoreName,
) => SECURITY_SCORE_STRENGTH[securityScoreName as SecurityScoreNameEnum] ?? {
  minStrength: undefined, maxStrength: undefined,
};

export * from './interfaces';
